import * as React from 'react';
import {
  FieldSettings,
  Filter,
  FilterChangeEvent,
  Operators,
} from '@progress/kendo-react-data-tools';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { saveNotificationsFilter } from '../../store/Notifications/NotificationsActions';
import { ASSET } from '../../utils/constants';
import { GetString } from '../StringResourceManager';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import esMessages from '../../utils/gridMessageKey.json';
import {
  getAlertPriorityOptions,
  getMaintenanceTypeOptions,
} from '../../utils/textMappings';
import { CustomFilterAlertsCategory } from './CustomFilterAlertsCategory';
import { TicketRecord } from '../../Interfaces/Tickets/TicketRecord';
import { PreventiveMaintenanceRecord } from '../../Interfaces/Maintenance/PreventiveMaintenanceRecord';
import { PredictiveMaintenanceRecord } from '../../Interfaces/Maintenance/PredictiveMaintenanceRecord';
import DynamicComboBox from '../DynamicComboBox';
loadMessages(esMessages, 'fr-CA');

const useStyles = makeStyles(() =>
  createStyles({
    filterArea: {
      Height: '100%',
      overflow: 'auto',
    },
  })
);

interface Props {
  notificationType: string;
}

const NotificationConditionsFilter: React.FC<Props> = ({
  notificationType,
}): React.ReactElement => {
  const classes = useStyles();
  const [fields, setFields] = React.useState<FieldSettings[] | null>(null);

  const FIELD_STORE_LABEL = GetString('FIELD_STORE_LABEL');
  const FIELD_LANE_LABEL = GetString('FIELD_LANE_LABEL');
  const FIELD_PRIORITY_LABEL = GetString('FILED_PRIORITY_LABEL');
  const FIELD_CATEGORY_LABEL = GetString('FIELD_CATEGORY_LABEL');
  const FIELD_MODEL_LABEL = GetString('MODEL');
  const FIELD_FAILURE_CODE_LABEL = GetString('FIELD_FAILURE_CODE_LABEL');
  const FIELD_PARTNAME_LABEL = GetString('COLUMN_TITLE_PART_NAME');
  const FIELD_ACTION_LABEL = GetString('ACTION_CATEGORY');
  const FIELD_MAINTENANCE_TYPE_LABEL = GetString(
    'FIELD_MAINTENANCE_TYPE_LABEL'
  );

  const dispatch: Dispatch<any> = useDispatch();

  const selectedFilter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.notificationsReducer.notificationsFilter
  );

  const siteNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumbers
  );

  const laneNumbers: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.laneNumbers
  );

  const tickets: TicketRecord[] = useSelector(
    (state: ApplicationState) => state.ticketsReducer.tickets
  );

  const prevMaintenanceData: PreventiveMaintenanceRecord[] = useSelector(
    (state: ApplicationState) => state.maintenanceReducer.prevMaintenanceRecords
  );

  const predMaintenanceData: PredictiveMaintenanceRecord[] = useSelector(
    (state: ApplicationState) => state.maintenanceReducer.predMaintenanceRecords
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const onFilterChange = (event: FilterChangeEvent) => {
    saveNotificationsFilter(dispatch, event.filter);
  };

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    switch (notificationType) {
      case 'Tickets':
        setFields([
          {
            name: ASSET.FIELD_STORE_NAME,
            label: FIELD_STORE_LABEL,
            filter: CustomSiteNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_LANE_NAME,
            label: FIELD_LANE_LABEL,
            filter: CustomLaneNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_CATEGORY_NAME,
            label: FIELD_CATEGORY_LABEL,
            filter: CustomTicketCategoryFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_MODEL_NAME,
            label: FIELD_MODEL_LABEL,
            filter: CustomTicketModelFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_FAILURE_CODE_NAME,
            label: FIELD_FAILURE_CODE_LABEL,
            filter: CustomTicketFailureCodeFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_PRIORITY_NAME,
            label: FIELD_PRIORITY_LABEL,
            filter: CustomAlertsPriorityFilter,
            operators: Operators.text.filter(e => e.operator === 'eq'),
          },
        ]);
        break;
      case 'DeviceIssues':
        setFields([
          {
            name: ASSET.FIELD_STORE_NAME,
            label: FIELD_STORE_LABEL,
            filter: CustomSiteNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_LANE_NAME,
            label: FIELD_LANE_LABEL,
            filter: CustomLaneNumberFilter,
            operators: Operators.text,
          },
        ]);
        break;
      case 'Alerts':
        setFields([
          {
            name: ASSET.FIELD_STORE_NAME,
            label: FIELD_STORE_LABEL,
            filter: CustomSiteNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_LANE_NAME,
            label: FIELD_LANE_LABEL,
            filter: CustomLaneNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_PRIORITY_NAME,
            label: FIELD_PRIORITY_LABEL,
            filter: CustomAlertsPriorityFilter,
            operators: Operators.text.filter(e => e.operator === 'eq'),
          },
          {
            name: ASSET.FIELD_CATEGORY_NAME,
            label: FIELD_CATEGORY_LABEL,
            filter: CustomFilterAlertsCategory,
            operators: Operators.text.filter(e => e.operator === 'eq'),
          },
        ]);
        break;
      case 'DMM':
        setFields([
          {
            name: ASSET.FIELD_MAINTENANCE_TYPE_NAME,
            label: FIELD_MAINTENANCE_TYPE_LABEL,
            filter: CustomDMMMaintenanceTypeFilter,
            operators: Operators.text.filter(e => e.operator === 'eq'),
          },
          {
            name: ASSET.FIELD_STORE_NUMBER,
            label: FIELD_STORE_LABEL,
            filter: CustomSiteNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_LANE_NAME,
            label: FIELD_LANE_LABEL,
            filter: CustomLaneNumberFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_MAINTENANCE_CATEGORY_NAME,
            label: FIELD_CATEGORY_LABEL,
            filter: CustomDMMCategoryFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_PARTNAME_NAME,
            label: FIELD_PARTNAME_LABEL,
            filter: CustomDMMPartnameFilter,
            operators: Operators.text,
          },
          {
            name: ASSET.FIELD_MAINTENANCE_ACTION_NAME,
            label: FIELD_ACTION_LABEL,
            filter: CustomDMMActionFilter,
            operators: Operators.text,
          },
        ]);
        break;
      default:
        setFields(null);
        break;
    }
  }, [notificationType]);

  const CustomSiteNumberFilter = (props: any) => {
    const onChangeSiteNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeSiteNumber}
        data={siteNumbers}
        value={props.filter.value}
        placeholder={GetString('STORE_PLACEHOLDER')}
      />
    );
  };

  const CustomLaneNumberFilter = (props: any) => {
    const onChangeLaneNumber = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeLaneNumber}
        data={laneNumbers}
        value={props.filter.value}
        placeholder={GetString('LANE_PLACEHOLDER')}
      />
    );
  };

  const CustomTicketCategoryFilter = (props: any) => {
    let ticketCategoryData = new Set(tickets.map(ticket => ticket.Category));
    const onChangeTicketsCategory = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: { ...props.filter, value: event.target.value },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeTicketsCategory}
        data={Array.from(ticketCategoryData)}
        value={props.filter.value}
        placeholder={GetString('CATEGORY_PLACEHOLDER')}
      />
    );
  };

  const CustomTicketModelFilter = (props: any) => {
    let ticketModelData = new Set(tickets.map(ticket => ticket.Model));
    const onChangeTicketsModel = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeTicketsModel}
        data={Array.from(ticketModelData)}
        value={props.filter.value}
        placeholder={GetString('MODEL_PLACEHOLDER')}
      />
    );
  };

  const CustomTicketFailureCodeFilter = (props: any) => {
    let ticketFaliureCodeData = new Set(
      tickets.map(ticket => ticket.FailureCode)
    );
    const onChangeTicketsFailureCode = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeTicketsFailureCode}
        data={Array.from(ticketFaliureCodeData)}
        value={props.filter.value}
        placeholder={GetString('FAILURE_CODE_PLACEHOLDER')}
      />
    );
  };

  const CustomAlertsPriorityFilter = (props: any) => {
    const onChangeAlertsPriority = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeAlertsPriority}
        data={getAlertPriorityOptions()}
        value={props.filter.value}
        placeholder={GetString('PRIORITY_PLACEHOLDER')}
      />
    );
  };

  const CustomDMMMaintenanceTypeFilter = (props: any) => {
    const onChangeDMMType = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDMMType}
        data={getMaintenanceTypeOptions()}
        value={props.filter.value}
        placeholder={GetString('SELECT_TYPE')}
      />
    );
  };

  const CustomDMMCategoryFilter = (props: any) => {
    let categoryData = new Set(
      prevMaintenanceData
        .map(record => record.UnitName)
        .concat(predMaintenanceData.map(record => record.UnitName))
    );
    const onChangeDMMCategory = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDMMCategory}
        data={Array.from(categoryData)}
        value={props.filter.value}
        placeholder={GetString('CATEGORY_PLACEHOLDER')}
      />
    );
  };

  const CustomDMMPartnameFilter = (props: any) => {
    let partnameData = new Set(
      prevMaintenanceData
        .map(record => record.PartName)
        .concat(predMaintenanceData.map(record => record.PartName))
    );
    const onChangeDMMPartname = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDMMPartname}
        data={Array.from(partnameData)}
        value={props.filter.value}
        placeholder={GetString('PARTNAME_PLACEHOLDER')}
      />
    );
  };

  const CustomDMMActionFilter = (props: any) => {
    let actionData = new Set(
      prevMaintenanceData
        .map(record => record.Recommendation)
        .concat(predMaintenanceData.map(record => record.Recommendation))
    );
    const onChangeDMMAction = (event: any) => {
      props.onFilterChange.call(undefined, {
        nextFilter: {
          ...props.filter,
          value: event.target.value,
        },
      });
    };
    return (
      <DynamicComboBox
        onChange={onChangeDMMAction}
        data={Array.from(actionData)}
        value={props.filter.value}
        placeholder={GetString('ACTION_PLACEHOLDER')}
      />
    );
  };

  return fields ? (
    <LocalizationProvider language={currentLocale.language}>
      <Filter
        style={{ maxHeight: '300px' }}
        value={selectedFilter}
        onChange={onFilterChange}
        className={classes.filterArea}
        fields={fields}
      />
    </LocalizationProvider>
  ) : (
    <></>
  );
};

export default NotificationConditionsFilter;
